import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import useIsMobile from '../hooks/useIsMobile';

export default function Pricing() {
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();
  const isMobile = useIsMobile();
  const [loading,    setLoading]    = useState(null);
  const [error,      setError]      = useState('');
  const [isReferred, setIsReferred] = useState(false);

  useEffect(() => {
    const code = sessionStorage.getItem('referralCode');
    if (code) setIsReferred(true);
  }, []);

  const plans = [
    {
      name: 'ResumePlus', price: '₹99', planType: 'resume_plus',
      desc: '3 analyses',
      features: ['ATS Score', 'Full Resume Improvements', 'Last 3 analyses saved'],
      bonus: '🎁 +1 bonus Resume analysis for you & your friend',
      highlight: false,
    },
    {
      name: 'ResumePro', price: '₹179', planType: 'resume_pro',
      desc: '7 analyses',
      features: ['ATS Score', 'Full Resume Improvements', 'Last 7 analyses saved'],
      bonus: '🎁 +1 bonus Resume analysis for you & your friend',
      highlight: false,
    },
    {
      name: 'InterviewPlus', price: '₹249', planType: 'interview_plus',
      desc: '3 analyses',
      features: ['ATS Score', 'Full Interview Prep', 'Last 3 analyses saved'],
      bonus: '🎁 +1 bonus Interview analysis for you & your friend',
      highlight: false,
    },
    {
      name: 'InterviewPro', price: '₹499', planType: 'interview_pro',
      desc: '7 analyses',
      features: ['ATS Score', 'Full Interview Prep', 'Last 7 analyses saved'],
      bonus: '🎁 +1 bonus Interview analysis for you & your friend',
      highlight: false,
    },
    {
      name: 'Elite', price: '₹699', planType: 'elite',
      desc: '7 analyses — everything',
      features: ['ATS Score', 'Full Resume Improvements', 'Full Interview Prep', 'Last 15 analyses saved'],
      bonus: '🎁 +1 bonus Resume + 1 bonus Interview for you & your friend',
      highlight: true,
    },
  ];

  const bonusMessages = {
    resume_plus:    '+1 bonus Resume analysis',
    resume_pro:     '+1 bonus Resume analysis',
    interview_plus: '+1 bonus Interview analysis',
    interview_pro:  '+1 bonus Interview analysis',
    elite:          '+1 bonus Resume + 1 bonus Interview analysis',
  };

  async function handlePurchase(planType) {
    if (!isSignedIn) {
      navigate('/sign-up');
      return;
    }
    setError('');
    setLoading(planType);
    try {
      const token = await getToken();
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + '/api/payments/create-order',
        { planType },
        { headers: { Authorization: 'Bearer ' + token } }
      );

      const options = {
        key:         data.keyId,
        amount:      data.amount,
        currency:    'INR',
        order_id:    data.orderId,
        name:        'CrackPrep',
        description: data.planName,
        theme:       { color: '#00D4AA' },
        handler: async function(response) {
          try {
            await axios.post(
              process.env.REACT_APP_API_URL + '/api/payments/verify-payment',
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                planType,
              },
              { headers: { Authorization: 'Bearer ' + token } }
            );

            // Clear cache
            sessionStorage.removeItem('referralCode');
            sessionStorage.removeItem('planInfo');

            const wasReferred = isReferred;
            setIsReferred(false);

            alert(
              'Payment successful! 🎉\n\n' +
              'Your plan has been activated.\n' +
              (wasReferred
                ? '\n🎁 Referral bonus credited!\n' +
                  bonusMessages[planType] + ' added to your account.\n' +
                  'Your friend also received the same bonus!\n'
                : '') +
              '\nHappy prepping!'
            );
            navigate('/analyze');
          } catch (err) {
            setError('Payment verification failed. Please contact support@crackprep.in');
          }
        },
        modal: { ondismiss: () => setLoading(null) }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(null);
    }
  }

  return (
    <div style={{ background: '#0A0F1E', minHeight: '100vh', color: 'white', fontFamily: 'Arial, sans-serif', padding: isMobile ? '1rem' : '1.5rem', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <span onClick={() => navigate('/')}
          style={{ color: '#00D4AA', fontWeight: 'bold', fontSize: '1.3rem', cursor: 'pointer' }}>
          CrackPrep
        </span>
        <button onClick={() => navigate(-1)}
          style={{ background: 'transparent', color: '#8899aa', border: '1px solid #1a2640', padding: '0.5rem 0.875rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
          ← Back
        </button>
      </div>

      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: isMobile ? '1.6rem' : '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Choose Your Plan
        </h1>
        <p style={{ color: '#8899aa', fontSize: isMobile ? '0.85rem' : '1rem' }}>
          All plans are one-time payments — no subscriptions, no hidden fees
        </p>
      </div>

      {/* Referral banner */}
      {isReferred && (
        <div style={{
          background: '#0d2018',
          border: '1px solid #00D4AA',
          borderRadius: '10px',
          padding: '1rem 1.5rem',
          marginBottom: '2rem',
          maxWidth: '700px',
          margin: '0 auto 2rem',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>🎁</div>
          <div style={{ color: '#00D4AA', fontWeight: 'bold', marginBottom: '0.4rem', fontSize: '1rem' }}>
            You were referred by a friend!
          </div>
          <div style={{ color: '#8899aa', fontSize: '0.85rem', lineHeight: 1.8 }}>
            Purchase any plan and <strong style={{ color: 'white' }}>both you and your friend</strong> get a bonus analysis — free.<br />
            <span style={{ color: '#00D4AA' }}>ResumePlus / ResumePro</span> → +1 bonus Resume analysis each<br />
            <span style={{ color: '#5b8cff' }}>InterviewPlus / InterviewPro</span> → +1 bonus Interview analysis each<br />
            <span style={{ color: '#f5a623' }}>Elite</span> → +1 bonus Resume + 1 bonus Interview each
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: '#1a0a0a', border: '1px solid #3a1010', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', color: '#ff6b6b', textAlign: 'center', maxWidth: '600px', margin: '0 auto 1.5rem', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      {/* Plans grid */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: isMobile ? '0.75rem' : '1.25rem',
        flexWrap: 'wrap',
        maxWidth: '1100px',
        margin: '0 auto',
      }}>
        {plans.map(plan => (
          <div key={plan.planType}
            style={{
              background: '#141B2D',
              border: '2px solid ' + (plan.highlight ? '#00D4AA' : '#1a2640'),
              borderRadius: '16px',
              padding: isMobile ? '1.25rem 1rem' : '2rem',
              width: isMobile ? 'calc(50% - 0.75rem)' : '190px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}>

            {plan.highlight && (
              <div style={{ position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)', background: '#00D4AA', color: '#0A0F1E', fontSize: '0.6rem', fontWeight: 'bold', padding: '0.2rem 0.75rem', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                BEST VALUE
              </div>
            )}

            <div style={{ color: '#00D4AA', fontWeight: 'bold', marginBottom: '0.2rem', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>{plan.name}</div>
            <div style={{ fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 'bold', marginBottom: '0.2rem' }}>{plan.price}</div>
            <div style={{ color: '#8899aa', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{plan.desc}</div>

            <div style={{ flex: 1, marginBottom: '0.75rem' }}>
              {plan.features.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <span style={{ color: '#00D4AA', fontSize: '0.8rem', marginTop: '1px' }}>✓</span>
                  <span style={{ color: '#8899aa', fontSize: isMobile ? '0.75rem' : '0.85rem', lineHeight: 1.4 }}>{f}</span>
                </div>
              ))}

              {/* Referral bonus line — only show when referred */}
              {isReferred && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: '1px solid #1a2640' }}>
                  <span style={{ fontSize: '0.75rem', marginTop: '1px' }}>🎁</span>
                  <span style={{ color: '#00D4AA', fontSize: isMobile ? '0.7rem' : '0.78rem', lineHeight: 1.4 }}>{plan.bonus}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => handlePurchase(plan.planType)}
              disabled={loading === plan.planType}
              style={{
                width: '100%',
                background: plan.highlight ? '#00D4AA' : 'transparent',
                color: plan.highlight ? '#0A0F1E' : '#00D4AA',
                border: '1px solid #00D4AA',
                padding: isMobile ? '0.6rem' : '0.75rem',
                borderRadius: '8px',
                cursor: loading === plan.planType ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                opacity: loading === plan.planType ? 0.7 : 1,
              }}>
              {loading === plan.planType ? 'Processing...' : 'Buy Now'}
            </button>
          </div>
        ))}
      </div>

      {/* Payment methods + legal */}
      <div style={{ textAlign: 'center', marginTop: '2rem', color: '#8899aa', fontSize: '0.8rem' }}>
        <p>Pay via UPI · Cards · Netbanking · Wallets</p>
        <p style={{ marginTop: '0.25rem' }}>Powered by Razorpay · 100% secure</p>
        <p style={{ marginTop: '0.75rem', fontSize: '0.75rem' }}>
          By purchasing you agree to our{' '}
          <span onClick={() => navigate('/terms')} style={{ color: '#00D4AA', cursor: 'pointer' }}>Terms & Conditions</span>
          {' '}and{' '}
          <span onClick={() => navigate('/privacy')} style={{ color: '#00D4AA', cursor: 'pointer' }}>Privacy Policy</span>
        </p>
      </div>

    </div>
  );
}